import { supabase } from '../lib/supabase'

export interface ProvisionPartner {
  id: string
  name: string
  email: string | null
  percentage: number
  is_active: boolean
  notes: string | null
  created_at: string
  updated_at: string
}

export interface ProvisionPayout {
  id: string
  provision_partner_id: string
  period_year: number
  period_month: number
  amount: number
  invoice_number: string | null
  invoice_received_at: string | null
  report_sent_at: string | null
  report_data: ProvisionReportData | null
  notes: string | null
  created_at: string
}

// Data structure for stored provision reports
export interface ProvisionReportData {
  partner_name: string
  partner_percentage: number
  total_sales: number
  total_provision_base: number
  total_provision: number
  customer_count: number
  line_items: ProvisionLineItem[]
  period_ref: string
  date_from: string
  date_to: string
  generated_at: string
}

export interface ProvisionLineItem {
  assignment_id: string
  assignment_number: number | null
  start_date: string
  customer_id: string
  customer_name: string
  customer_address: string
  article_id: string
  article_name: string
  article_number: string
  unit: string
  quantity: number
  unit_price: number
  total_price: number
  provision_percentage: number
  provision_amount: number
}

export interface ProvisionSummary {
  partner_id: string
  partner_name: string
  partner_email: string | null
  partner_percentage: number
  total_sales: number           // All sales for customers linked to this partner
  total_provision_base: number  // Only provision articles
  total_provision: number       // Calculated provision amount
  line_items: ProvisionLineItem[]
  customer_count: number
  paid_periods: { year: number; month: number }[]
}

// Swedish month names
const MONTH_NAMES = [
  'januari', 'februari', 'mars', 'april', 'maj', 'juni',
  'juli', 'augusti', 'september', 'oktober', 'november', 'december'
]

export function getMonthName(month: number): string {
  return MONTH_NAMES[month - 1] || ''
}

export function formatPeriod(year: number, month: number): string {
  return `${getMonthName(month)} ${year}`
}

// Fetch all provision partners
export async function fetchProvisionPartners(includeInactive = false): Promise<ProvisionPartner[]> {
  let query = supabase
    .from('provision_partners')
    .select('*')
    .order('name')

  if (!includeInactive) {
    query = query.eq('is_active', true)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching provision partners:', error)
    throw error
  }

  return data || []
}

// Fetch a single provision partner
export async function fetchProvisionPartner(id: string): Promise<ProvisionPartner | null> {
  const { data, error } = await supabase
    .from('provision_partners')
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    console.error('Error fetching provision partner:', error)
    return null
  }

  return data
}

// Create a provision partner
export async function createProvisionPartner(partner: Omit<ProvisionPartner, 'id' | 'created_at' | 'updated_at'>): Promise<ProvisionPartner> {
  const { data, error } = await supabase
    .from('provision_partners')
    .insert(partner)
    .select()
    .single()

  if (error) {
    console.error('Error creating provision partner:', error)
    throw error
  }

  return data
}

// Update a provision partner
export async function updateProvisionPartner(id: string, updates: Partial<ProvisionPartner>): Promise<ProvisionPartner> {
  const { data, error } = await supabase
    .from('provision_partners')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('Error updating provision partner:', error)
    throw error
  }

  return data
}

// Delete a provision partner
export async function deleteProvisionPartner(id: string): Promise<void> {
  const { error } = await supabase
    .from('provision_partners')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting provision partner:', error)
    throw error
  }
}

// Fetch provision payouts for a partner
export async function fetchProvisionPayouts(partnerId?: string): Promise<(ProvisionPayout & { partner_name: string })[]> {
  let query = supabase
    .from('provision_payouts')
    .select(`
      *,
      provision_partners!inner(name)
    `)
    .order('period_year', { ascending: false })
    .order('period_month', { ascending: false })

  if (partnerId) {
    query = query.eq('provision_partner_id', partnerId)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching provision payouts:', error)
    throw error
  }

  return (data || []).map(row => ({
    ...row,
    partner_name: (row.provision_partners as { name: string }).name
  }))
}

// Create a provision payout
export async function createProvisionPayout(payout: Omit<ProvisionPayout, 'id' | 'created_at'>): Promise<ProvisionPayout> {
  const { data, error } = await supabase
    .from('provision_payouts')
    .insert(payout)
    .select()
    .single()

  if (error) {
    console.error('Error creating provision payout:', error)
    throw error
  }

  return data
}

// Update a provision payout
export async function updateProvisionPayout(id: string, updates: Partial<ProvisionPayout>): Promise<ProvisionPayout> {
  const { data, error } = await supabase
    .from('provision_payouts')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('Error updating provision payout:', error)
    throw error
  }

  return data
}

// Delete a provision payout (only allowed if report hasn't been sent)
export async function deleteProvisionPayout(id: string): Promise<void> {
  // First check if report has been sent
  const { data: payout, error: fetchError } = await supabase
    .from('provision_payouts')
    .select('report_sent_at')
    .eq('id', id)
    .single()

  if (fetchError) {
    console.error('Error fetching payout:', fetchError)
    throw fetchError
  }

  if (payout?.report_sent_at) {
    throw new Error('Kan inte ta bort en post där underlag redan skickats')
  }

  const { error } = await supabase
    .from('provision_payouts')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting provision payout:', error)
    throw error
  }
}

// Create a provision report draft - saves report data but doesn't mark as sent
export async function createProvisionReportDraft(
  summary: ProvisionSummary,
  dateFrom: string,
  dateTo: string
): Promise<ProvisionPayout> {
  const periodYear = new Date(dateFrom).getFullYear()
  const periodMonth = new Date(dateFrom).getMonth() + 1
  const periodRef = `PROV-${dateFrom.substring(0, 7)}`

  const reportData: ProvisionReportData = {
    partner_name: summary.partner_name,
    partner_percentage: summary.partner_percentage,
    total_sales: summary.total_sales,
    total_provision_base: summary.total_provision_base,
    total_provision: summary.total_provision,
    customer_count: summary.customer_count,
    line_items: summary.line_items,
    period_ref: periodRef,
    date_from: dateFrom,
    date_to: dateTo,
    generated_at: new Date().toISOString()
  }

  // Check if a payout record already exists for this partner and period
  const { data: existing, error: fetchError } = await supabase
    .from('provision_payouts')
    .select('id, report_sent_at')
    .eq('provision_partner_id', summary.partner_id)
    .eq('period_year', periodYear)
    .eq('period_month', periodMonth)
    .maybeSingle()

  if (fetchError) {
    console.error('Error checking existing payout:', fetchError)
    throw fetchError
  }

  if (existing) {
    // Update existing record (only if not already sent)
    if (existing.report_sent_at) {
      throw new Error('Underlag har redan skickats för denna period')
    }

    const { data, error } = await supabase
      .from('provision_payouts')
      .update({
        amount: summary.total_provision,
        report_data: reportData
        // Don't set report_sent_at - user must confirm manually
      })
      .eq('id', existing.id)
      .select()
      .single()

    if (error) {
      console.error('Error updating payout:', error)
      throw error
    }

    return data
  } else {
    // Create new record as draft
    const { data, error } = await supabase
      .from('provision_payouts')
      .insert({
        provision_partner_id: summary.partner_id,
        period_year: periodYear,
        period_month: periodMonth,
        amount: summary.total_provision,
        report_data: reportData
        // Don't set report_sent_at - user must confirm manually
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating payout:', error)
      throw error
    }

    return data
  }
}

// Mark a provision report as sent
export async function markProvisionReportSent(payoutId: string): Promise<ProvisionPayout> {
  const { data, error } = await supabase
    .from('provision_payouts')
    .update({
      report_sent_at: new Date().toISOString()
    })
    .eq('id', payoutId)
    .select()
    .single()

  if (error) {
    console.error('Error marking report as sent:', error)
    throw error
  }

  return data
}

// Calculate provisions for a date range
export async function calculateProvisions(
  dateFrom: string,
  dateTo: string,
  partnerId?: string
): Promise<ProvisionSummary[]> {
  // 1. Get customers with provision partners
  let customerQuery = supabase
    .from('customers')
    .select('id, full_name, address_street, address_postal_code, address_city, provision_partner_id')
    .not('provision_partner_id', 'is', null)

  if (partnerId) {
    customerQuery = customerQuery.eq('provision_partner_id', partnerId)
  }

  const { data: customers, error: customersError } = await customerQuery

  if (customersError) {
    console.error('Error fetching customers with provision partners:', customersError)
    throw customersError
  }

  if (!customers || customers.length === 0) {
    return []
  }

  const customerIds = customers.map(c => c.id)

  // 2. Get invoiced assignments for these customers in the date range
  // Filter on customer_invoice_status = 'fakturerad' (customer has been invoiced)
  const { data: assignments, error: assignmentsError } = await supabase
    .from('assignments')
    .select(`
      id,
      assignment_number,
      start_date,
      customer_id,
      admin_reported_articles,
      assignment_articles(
        quantity,
        custom_price,
        article:articles(
          id,
          article_number,
          name,
          unit,
          is_provision_article
        )
      )
    `)
    .in('customer_id', customerIds)
    .eq('customer_invoice_status', 'fakturerad')
    .gte('start_date', dateFrom)
    .lte('start_date', dateTo)

  if (assignmentsError) {
    console.error('Error fetching assignments:', assignmentsError)
    throw assignmentsError
  }

  // 3. Get provision partners
  const { data: partners, error: partnersError } = await supabase
    .from('provision_partners')
    .select('*')
    .eq('is_active', true)

  if (partnersError) {
    console.error('Error fetching provision partners:', partnersError)
    throw partnersError
  }

  // 4. Get default customer price list for pricing
  const { data: defaultPriceList, error: priceListError } = await supabase
    .from('price_lists')
    .select('id')
    .eq('type', 'customer')
    .eq('is_default', true)
    .single()

  if (priceListError && priceListError.code !== 'PGRST116') {
    console.error('Error fetching default price list:', priceListError)
  }

  // 5. Get article prices from the default price list
  let articlePrices: Record<string, number> = {}
  if (defaultPriceList) {
    const { data: prices, error: pricesError } = await supabase
      .from('price_list_articles')
      .select('article_id, price_excl_vat')
      .eq('price_list_id', defaultPriceList.id)

    if (pricesError) {
      console.error('Error fetching article prices:', pricesError)
    } else if (prices) {
      articlePrices = Object.fromEntries(prices.map(p => [p.article_id, p.price_excl_vat]))
    }
  }

  // 5b. Collect admin-reported article IDs and fetch their provision status
  const adminArticleIds = new Set<string>()
  for (const assignment of assignments || []) {
    const adminArticles = (assignment.admin_reported_articles as any[]) || []
    for (const ra of adminArticles) {
      if (ra.article_id && !ra.is_expense) {
        adminArticleIds.add(ra.article_id)
      }
    }
  }

  const adminArticleInfoMap: Record<string, { is_provision_article: boolean; article_number: string; name: string; unit: string }> = {}
  if (adminArticleIds.size > 0) {
    const { data: adminArticleData } = await supabase
      .from('articles')
      .select('id, is_provision_article, article_number, name, unit')
      .in('id', Array.from(adminArticleIds))

    for (const a of adminArticleData || []) {
      adminArticleInfoMap[a.id] = {
        is_provision_article: a.is_provision_article || false,
        article_number: a.article_number || '',
        name: a.name,
        unit: a.unit || 'st'
      }
    }
  }

  // 5c. Get TG reports for reported quantities
  const { data: tgReports } = await supabase
    .from('assignment_subcontractors')
    .select('assignment_id, reported_articles')
    .in('assignment_id', (assignments || []).map(a => a.id))
    .in('tg_status', ['godkand', 'avsagt'])

  // Build reported quantity map per assignment: { assignmentId: { articleId: totalQuantity } }
  const reportedQuantityByAssignment: Record<string, Record<string, number>> = {}
  for (const tgReport of tgReports || []) {
    if (!reportedQuantityByAssignment[tgReport.assignment_id]) {
      reportedQuantityByAssignment[tgReport.assignment_id] = {}
    }
    const reportedArticles = (tgReport.reported_articles as any[]) || []
    for (const ra of reportedArticles) {
      if (!ra.is_expense && ra.article_id) {
        const map = reportedQuantityByAssignment[tgReport.assignment_id]
        map[ra.article_id] = (map[ra.article_id] || 0) + (ra.actual_quantity || 0)
      }
    }
  }

  // 6. Get paid periods
  const { data: payouts, error: payoutsError } = await supabase
    .from('provision_payouts')
    .select('provision_partner_id, period_year, period_month')

  if (payoutsError) {
    console.error('Error fetching payouts:', payoutsError)
  }

  const paidPeriodsByPartner: Record<string, { year: number; month: number }[]> = {}
  for (const payout of payouts || []) {
    if (!paidPeriodsByPartner[payout.provision_partner_id]) {
      paidPeriodsByPartner[payout.provision_partner_id] = []
    }
    paidPeriodsByPartner[payout.provision_partner_id].push({
      year: payout.period_year,
      month: payout.period_month
    })
  }

  // 7. Build summaries per partner
  const summariesByPartner: Record<string, ProvisionSummary> = {}

  for (const partner of partners || []) {
    summariesByPartner[partner.id] = {
      partner_id: partner.id,
      partner_name: partner.name,
      partner_email: partner.email,
      partner_percentage: partner.percentage,
      total_sales: 0,
      total_provision_base: 0,
      total_provision: 0,
      line_items: [],
      customer_count: 0,
      paid_periods: paidPeriodsByPartner[partner.id] || []
    }
  }

  // Track unique customers per partner
  const uniqueCustomersPerPartner: Record<string, Set<string>> = {}

  for (const assignment of assignments || []) {
    const customer = customers.find(c => c.id === assignment.customer_id)
    if (!customer || !customer.provision_partner_id) continue

    const partnerId = customer.provision_partner_id
    const partner = partners?.find(p => p.id === partnerId)
    if (!partner) continue

    if (!uniqueCustomersPerPartner[partnerId]) {
      uniqueCustomersPerPartner[partnerId] = new Set()
    }
    uniqueCustomersPerPartner[partnerId].add(customer.id)

    // Format customer address (used for provision articles)
    const customerAddress = [
      customer.address_street,
      `${customer.address_postal_code} ${customer.address_city}`
    ].filter(Boolean).join(', ')

    // Get reported quantities for this assignment
    const assignmentReportedQty = reportedQuantityByAssignment[assignment.id] || {}

    for (const assignmentArticle of (assignment.assignment_articles as any[]) || []) {
      const article = assignmentArticle.article
      if (!article) continue

      // Get price: custom_price overrides, otherwise use price list
      const unitPrice = assignmentArticle.custom_price ?? articlePrices[article.id] ?? 0
      // Use reported quantity if available, fall back to planned quantity
      const quantity = assignmentReportedQty[article.id] ?? assignmentArticle.quantity ?? 0
      const totalPrice = unitPrice * quantity

      // Add to total sales for ALL articles
      summariesByPartner[partnerId].total_sales += totalPrice

      // Only process provision articles for provision calculation
      if (!article.is_provision_article) continue

      const provisionAmount = totalPrice * (partner.percentage / 100)

      const lineItem: ProvisionLineItem = {
        assignment_id: assignment.id,
        assignment_number: assignment.assignment_number,
        start_date: assignment.start_date,
        customer_id: customer.id,
        customer_name: customer.full_name,
        customer_address: customerAddress,
        article_id: article.id,
        article_name: article.name,
        article_number: article.article_number,
        unit: article.unit || 'st',
        quantity,
        unit_price: unitPrice,
        total_price: totalPrice,
        provision_percentage: partner.percentage,
        provision_amount: provisionAmount
      }

      summariesByPartner[partnerId].line_items.push(lineItem)
      summariesByPartner[partnerId].total_provision_base += totalPrice
      summariesByPartner[partnerId].total_provision += provisionAmount
    }

    // Process admin-reported articles (centrally delivered items)
    const adminArticles = (assignment.admin_reported_articles as any[]) || []
    for (const ra of adminArticles) {
      if (ra.is_expense || !ra.article_id) continue

      const unitPrice = articlePrices[ra.article_id] ?? 0
      const quantity = ra.actual_quantity || 0
      const totalPrice = unitPrice * quantity

      // Add to total sales
      summariesByPartner[partnerId].total_sales += totalPrice

      // Check if this is a provision article using pre-fetched article info
      const articleInfo = adminArticleInfoMap[ra.article_id]
      if (!articleInfo || !articleInfo.is_provision_article) continue

      const provisionAmount = totalPrice * (partner.percentage / 100)

      const lineItem: ProvisionLineItem = {
        assignment_id: assignment.id,
        assignment_number: assignment.assignment_number,
        start_date: assignment.start_date,
        customer_id: customer.id,
        customer_name: customer.full_name,
        customer_address: customerAddress,
        article_id: ra.article_id,
        article_name: articleInfo.name,
        article_number: articleInfo.article_number,
        unit: articleInfo.unit,
        quantity,
        unit_price: unitPrice,
        total_price: totalPrice,
        provision_percentage: partner.percentage,
        provision_amount: provisionAmount
      }

      summariesByPartner[partnerId].line_items.push(lineItem)
      summariesByPartner[partnerId].total_provision_base += totalPrice
      summariesByPartner[partnerId].total_provision += provisionAmount
    }
  }

  // Set customer counts
  for (const [partnerId, customerSet] of Object.entries(uniqueCustomersPerPartner)) {
    if (summariesByPartner[partnerId]) {
      summariesByPartner[partnerId].customer_count = customerSet.size
    }
  }

  // Filter out partners with no line items if a specific partner wasn't requested
  const results = Object.values(summariesByPartner)
  if (partnerId) {
    return results
  }
  return results.filter(s => s.line_items.length > 0)
}

// Get customer count per partner
export async function getCustomerCountsPerPartner(): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from('customers')
    .select('provision_partner_id')
    .not('provision_partner_id', 'is', null)

  if (error) {
    console.error('Error fetching customer counts:', error)
    return {}
  }

  const counts: Record<string, number> = {}
  for (const row of data || []) {
    if (row.provision_partner_id) {
      counts[row.provision_partner_id] = (counts[row.provision_partner_id] || 0) + 1
    }
  }

  return counts
}

// Check if a period is already paid for a partner
export function isPeriodPaid(
  paidPeriods: { year: number; month: number }[],
  year: number,
  month: number
): boolean {
  return paidPeriods.some(p => p.year === year && p.month === month)
}
